/**
 * Drives the loading spinner for the Blog Category Grid section's infinite
 * scroll. `results-list` (assets/results-list.js) handles fetching and
 * appending pages itself but renders no loading feedback, so this watches
 * the same DOM for state changes and shows/hides a spinner accordingly.
 */
class BlogCategoryGridLoader {
  /** @param {HTMLElement} root */
  constructor(root) {
    const grid = root.querySelector('[ref="grid"]');
    const loader = root.querySelector('[ref="viewMoreNext"]');
    if (!(grid instanceof HTMLElement) || !(loader instanceof HTMLElement)) return;

    this.grid = grid;
    this.loader = loader;
    this.lastPage = Number(grid.dataset.lastPage || 1);

    this.intersectionObserver = new IntersectionObserver(this.#handleIntersect, { rootMargin: '100px' });
    this.intersectionObserver.observe(loader);

    this.mutationObserver = new MutationObserver(this.#handleGridChange);
    this.mutationObserver.observe(grid, { childList: true });

    this.#updateExhausted();
  }

  #maxLoadedPage() {
    const cards = this.grid.querySelectorAll('[ref="cards[]"]');
    const last = cards[cards.length - 1];
    return last instanceof HTMLElement ? Number(last.dataset.page || 1) : 1;
  }

  #updateExhausted() {
    this.loader.classList.toggle('is-exhausted', this.#maxLoadedPage() >= this.lastPage);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting && this.#maxLoadedPage() < this.lastPage) {
        this.loader.classList.add('is-loading');
      }
    }
  };

  #handleGridChange = () => {
    this.loader.classList.remove('is-loading');
    this.#updateExhausted();
  };
}

for (const root of document.querySelectorAll('[data-blog-category-grid]')) {
  new BlogCategoryGridLoader(root);
}
